import hoodieJson from './hoodie.json';
import longSleeveJson from './long-sleeve.json';
import teeJson from './t-shirt.json';
import { productConfigSchema } from '../domain/schemas';
import type { ProductColour, ProductConfig } from '../domain/types';
import { resolveProductVariant } from './size-profiles';

export {
  isSizeSelectable,
  resolveDefaultSize,
  resolveGarmentSelection,
  resolveGarmentVisualScale,
  resolvePrintableArea,
  resolveProductVariant,
  resolveSelectableSizes,
  resolveSizeProfile,
} from './size-profiles';
export type { GarmentVisualScale, ResolvedGarmentSelection } from './size-profiles';

export function parseProductConfig(input: unknown): ProductConfig {
  return productConfigSchema.parse(input) as ProductConfig;
}

/** Every locally defined product, including products that are not enabled in the configurator yet. */
export const productCatalogue: ProductConfig[] = [teeJson, hoodieJson, longSleeveJson].map(
  parseProductConfig,
);

/** Products currently available to customers in the configurator. */
export const products: ProductConfig[] = productCatalogue.filter(
  product => product.garmentType === 't-shirt',
);

export function resolveVariantColours(product: ProductConfig, variantId?: string): ProductColour[] {
  const variant = resolveProductVariant(product, variantId) ?? product.variants[0];
  if (!variant?.colourIds) return product.colours;
  const availableIds = new Set(variant.colourIds);
  return product.colours.filter(colour => availableIds.has(colour.id));
}

export function isColourAvailableForVariant(
  product: ProductConfig,
  variantId: string,
  colourId: string,
): boolean {
  return resolveVariantColours(product, variantId).some(colour => colour.id === colourId);
}

export function getDefaultProductColour(product: ProductConfig, variantId?: string): ProductColour {
  const variant = resolveProductVariant(product, variantId) ?? product.variants[0];
  const colours = resolveVariantColours(product, variant?.id);
  const defaultColourId = variant?.defaultColourId ?? product.defaultColourId;
  const colour = colours.find(item => item.id === defaultColourId) ?? colours[0];
  if (!colour) throw new Error(`Product ${product.id} has no colours`);
  return colour;
}

export function resolveProductColour(
  product: ProductConfig,
  colourId?: string,
  variantId?: string,
): ProductColour {
  return (
    resolveVariantColours(product, variantId).find(item => item.id === colourId) ??
    getDefaultProductColour(product, variantId)
  );
}

export async function loadProductConfig(url: string, signal?: AbortSignal): Promise<ProductConfig> {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`Product config failed: ${response.status}`);
  return parseProductConfig(await response.json());
}
